import requestService from '../requestsService';
import { FoodResponseDto } from '@bitecritic-api/src/foods/dto/food.response.dto';
import { GetFoodByIdResponseDTO } from '@bitecritic-api/src/foods/dto/food.dto';
import { ManageFoodResponseDto } from '@bitecritic-api/src/management/foods/dto/manage-food.response.dto';
import { ManageFoodRequestDto } from '@bitecritic-api/src/management/foods/dto/manage-food.request.dto';
import { statutesEnum } from '@bitecritic-api/src/statuses/statutes.enum';

const basePath = '/foods' as const;
const managementBasePath = '/management/foods' as const;

export interface GetFoodsParams {
  search?: string;
  language?: string;
  page?: number;
  limit?: number;
  categoryId?: number;
  period?: 'ALL_TIME' | 'LAST_WEEK' | 'LAST_MONTH' | 'LAST_YEAR';
  sort?:
    | 'name-asc'
    | 'name-desc'
    | 'reviewOverallRating-desc'
    | 'reviewOverallRating-asc'
    | 'reviewCount-desc'
    | 'reviewCount-asc';
}

export interface GetManagementFoodsParams {
  search?: string;
  page?: number;
  limit?: number;
  categoryId?: number;
  status?: (typeof statutesEnum)[keyof typeof statutesEnum];
  sort?:
    | 'name-asc'
    | 'name-desc'
    | 'reviewOverallRating-desc'
    | 'reviewOverallRating-asc'
    | 'reviewCount-desc'
    | 'reviewCount-asc'
    | 'status-asc'
    | 'status-desc'
    | 'id-asc'
    | 'id-desc'
    | 'updatedAt-asc'
    | 'updatedAt-desc';
}

interface FoodsListResponse {
  items: FoodResponseDto[];
  total: number;
  page: number;
  totalPages: number;
  hasMore: boolean;
}

interface ManagementFoodsListResponse {
  data: ManageFoodResponseDto[];
  total: number;
  page: number;
  totalPages: number;
  hasMore: boolean;
}

export const fetchFoodsByCategory = async (
  categoryId: number,
  lang: string,
): Promise<FoodResponseDto[] | null> => {
  if (!categoryId || !Number.isInteger(categoryId) || categoryId <= 0) {
    return null;
  }

  const response = await requestService.fetch<FoodResponseDto[]>(
    `${basePath}/category/${categoryId}`,
    {
      params: { lang },
    },
  );
  return response ?? [];
};

export const fetchFoods = async (
  searchTerm: string,
  language: string,
  limit = 20,
): Promise<FoodsListResponse> => {
  if (!searchTerm || searchTerm.length < 3) {
    return {
      items: [],
      total: 0,
      page: 1,
      totalPages: 1,
      hasMore: false,
    };
  }
  const response = await requestService.fetch<FoodsListResponse>(basePath, {
    params: {
      search: searchTerm,
      language,
      limit,
    },
  });
  return (
    response ?? {
      items: [],
      total: 0,
      page: 1,
      totalPages: 1,
      hasMore: false,
    }
  );
};

export const getFoods = async (
  params: GetFoodsParams = {},
): Promise<FoodsListResponse> => {
  const response = await requestService.fetch<FoodsListResponse>(basePath, {
    params: {
      search: params.search || '',
      language: params.language || 'en',
      page: params.page || 1,
      limit: params.limit || 20,
      categoryId: params.categoryId,
      sort: params.sort,
      period: params.period || 'ALL_TIME',
    },
  });

  return (
    response ?? {
      items: [],
      total: 0,
      page: 1,
      totalPages: 1,
      hasMore: false,
    }
  );
};

export const getFoodById = async (id: number, lang = 'en') => {
  const response = await requestService.fetch<GetFoodByIdResponseDTO>(
    `${basePath}/${id}`,
    {
      params: { lang },
    },
  );
  return response ?? null;
};

// Management-specific methods
export const getManagementFoods = async (
  params: GetManagementFoodsParams = {},
): Promise<ManagementFoodsListResponse> => {
  const response = await requestService.fetch<ManagementFoodsListResponse>(
    managementBasePath,
    {
      qs: {
        search: params.search || '',
        page: params.page || 1,
        limit: params.limit || 20,
        categoryId: params.categoryId,
        status: params.status,
        sort: params.sort || 'id-desc',
      },
    },
  );

  return (
    response ?? {
      data: [],
      total: 0,
      page: 1,
      totalPages: 1,
      hasMore: false,
    }
  );
};

export const getManagementFoodById = async (
  id: number,
): Promise<ManageFoodResponseDto> => {
  const response = await requestService.fetch<ManageFoodResponseDto>(
    `${managementBasePath}/${id}`,
  );
  if (!response) {
    throw new Error(`Food with ID ${id} not found`);
  }
  return response;
};

export const createFood = async (
  data: ManageFoodRequestDto,
): Promise<ManageFoodResponseDto> => {
  const response = await requestService.post<ManageFoodResponseDto>(
    managementBasePath,
    data,
  );
  if (!response) {
    throw new Error('Failed to create food');
  }
  return response;
};

export const updateFood = async (
  id: number,
  data: ManageFoodRequestDto,
): Promise<ManageFoodResponseDto> => {
  const response = await requestService.patch<ManageFoodResponseDto>(
    `${managementBasePath}/${id}`,
    data,
  );
  if (!response) {
    throw new Error(`Failed to update food with ID ${id}`);
  }
  return response;
};

export const deleteFood = async (id: number): Promise<void> => {
  await requestService.fetch(`${managementBasePath}/${id}`, {
    method: 'DELETE',
  });
};

export const updateFoodStatus = async (
  id: number,
  status: (typeof statutesEnum)[keyof typeof statutesEnum],
): Promise<ManageFoodResponseDto> => {
  const response = await requestService.patch<ManageFoodResponseDto>(
    `${managementBasePath}/${id}/status`,
    { status },
  );
  if (!response) {
    throw new Error(`Failed to update status for food with ID ${id}`);
  }
  return response;
};

const foodsService = {
  fetchFoodsByCategory,
  fetchFoods,
  getFoods,
  getFoodById,
  getManagementFoods,
  getManagementFoodById,
  createFood,
  updateFood,
  deleteFood,
  updateFoodStatus,
};

export default foodsService;
